import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import config from 'constants/config'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import type { FlightDropdownOption } from 'components/Flights/types'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import PaletteSheet from 'LuxKit/PaletteSheet'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import useCountdownTagOptions from 'home/hooks/useCountdownTagOptions'
import FlightDealPoints from './FlightDealPoints'

const FlightCardContainer = styled(Clickable)`
  display: grid;
  grid-template-rows: ${rem(155)} 1fr;
  position: relative;
  width: ${rem(300)};
  overflow: hidden;
  transition: background-color 0.2s;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`

const DealContainer = styled(Group)`
  padding: ${rem(8)};
`

const PriceSplitContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${rem(12)};
`

const PriceColumn = styled.div<{ align: 'left' | 'right' }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: ${props => props.align === 'right' ? 'flex-end' : 'flex-start'};
  text-align: ${props => props.align};
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
`

const CarrierContainer = styled.div`
  position: absolute;
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  bottom: ${rem(12)};
  left: ${rem(8)};
`

const CarrierImage = styled(Image)`
  max-width: ${rem(80)};
`

const LabelContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(4)};
  left: ${rem(4)};
`

interface Props {
  flightDeal: App.FlightDeal
  position?: number
  hasMultipleAirlines: boolean
  economyDeal?: App.FlightDeal
  businessDeal?: App.FlightDeal
}

interface FareDetails {
  fareClassLabel?: string
  fareTypeLabel?: string
  price: number
}

interface PriceDetailProps {
  align: 'left' | 'right'
  fareDetails?: FareDetails
}

function getFareDetails(
  deal: App.FlightDeal | undefined,
  fareTypes: Array<FlightDropdownOption>,
  fareCabins: Array<FlightDropdownOption>,
): FareDetails | undefined {
  if (!deal) {
    return undefined
  }

  const fareTypeLabel = fareTypes.find(type => type.value === deal.fareType)?.label
  const fareClassLabel = fareCabins.find(type => type.value === deal.fareClass)?.label

  return {
    fareClassLabel,
    fareTypeLabel,
    price: deal.perAdultPrice,
  }
}

function PriceDetail(props: PriceDetailProps) {
  const { align, fareDetails } = props
  const intl = useIntl()

  if (!fareDetails) {
    return null
  }

  return (
    <PriceColumn align={align}>
      <PriceRowPriceCaption>
        {intl.formatMessage(
          {
            id: 'Lo4fTqyPFfBt0j3irWG0',
            defaultMessage: '{fareClass} {fareType} from',
            description: 'Price caption showing fare class and type for a flight deal tile',
          },
          { fareClass: fareDetails.fareClassLabel, fareType: fareDetails.fareTypeLabel },
        )}
      </PriceRowPriceCaption>
      <PriceRowPrice
        size="L"
        price={fareDetails.price}
        saleUnit="adult"
      />
    </PriceColumn>
  )
}

function FlightDealImageTile(props: Props) {
  const { flightDeal, position, hasMultipleAirlines, economyDeal, businessDeal } = props
  const intl = useIntl()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const { fareTypes, fareCabins } = useFlightFareLabels()
  const dateFormatter = useIntlDateFormatter()
  const countdownOptions = useCountdownTagOptions({ tagPlaceId: 'deals-tile-item' })
  const primaryFareDetails = useMemo(() => getFareDetails(flightDeal, fareTypes, fareCabins), [flightDeal, fareTypes, fareCabins])
  const economyFareDetails = useMemo(() => getFareDetails(economyDeal, fareTypes, fareCabins), [economyDeal, fareTypes, fareCabins])
  const businessFareDetails = useMemo(() => getFareDetails(businessDeal, fareTypes, fareCabins), [businessDeal, fareTypes, fareCabins])
  const showSplitPricing = useMemo(() => Boolean(economyFareDetails && businessFareDetails), [economyFareDetails, businessFareDetails])
  const priceContent = useMemo(() => {
    if (showSplitPricing) {
      return (
        <PriceSplitContainer>
          <PriceDetail align="left" fareDetails={economyFareDetails} />
          <PriceDetail align="right" fareDetails={businessFareDetails} />
        </PriceSplitContainer>
      )
    }

    return (
      <PriceDetail align="left" fareDetails={primaryFareDetails} />
    )
  }, [businessFareDetails, economyFareDetails, primaryFareDetails, showSplitPricing])

  const showFullDateLabel = useMemo(() => {
    const end = moment(flightDeal.salesEndDate)
    const leftToTheEnd = moment.duration(end.diff(moment()))
    const roundedEndDays = Math.floor(leftToTheEnd.asDays())
    return roundedEndDays > countdownOptions.showWithinXDays
  }, [flightDeal.salesEndDate, countdownOptions.showWithinXDays])
  const saleEndsIcon = useMemo(() => <LineAwardIcon />, [])
  const saleEndsLabel = useMemo(() => {
    if (!showFullDateLabel) {
      return null
    }

    return (
      <PromotionLabel variant="default" icon={saleEndsIcon}>
        {intl.formatMessage(
          {
            id: 'IWBAyr1O3whtoFvwLZPA',
            defaultMessage: 'Sale ends {endDate}',
            description: 'Promotion label showing sale end date on flight deal image tile',
          },
          { endDate: dateFormatter(flightDeal.salesEndDate, INTL_DMY_CASUAL_SHORT_FORMAT) },
        )}
      </PromotionLabel>
    )
  }, [dateFormatter, flightDeal.salesEndDate, intl, saleEndsIcon, showFullDateLabel])

  const [inViewRef, hasBeenInView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  useEffect(() => {
    if (hasBeenInView && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: flightDeal,
        position,
        key: EventDataKey.FlightImpression,
      })
    }
  }, [hasBeenInView, flightDeal, position, dispatchOfferListEvent])

  const onClick = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: flightDeal,
        position,
        key: EventDataKey.FlightClick,
      })
    }
  }, [position, flightDeal, dispatchOfferListEvent])

  const hasCarrierLogo = flightDeal.carrier.logo?.id || flightDeal.carrier.logo?.url

  return (
    <FlightCardContainer
      to={`/flight/deal/${flightDeal.id}`}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
      ref={inViewRef}
      onClick={onClick}
    >
      {flightDeal.heroImage?.id && (
        <ImageContainer paletteType="default">
          <Image
            aspectRatio="3:2"
            id={flightDeal.heroImage.id}
            fit="center"
            dpr={2}
            height="155"
            width="300"
          />
          <LabelContainer>
            <OfferUrgencyCountdownLabel
              endDate={flightDeal.salesEndDate}
              tagPlace="deals-tile-item"
              fallback={saleEndsLabel ?? undefined}
            />
          </LabelContainer>
          {hasMultipleAirlines && hasCarrierLogo && (
            <CarrierContainer>
              <CarrierImage
                height={34}
                fit="center"
                image={flightDeal.carrier.logo}
                alt={intl.formatMessage(
                  {
                    id: 'eOYj4lCm3wJdlngc2npv',
                    defaultMessage: 'Logo of {carrierName}',
                    description: 'Alt text for airline carrier logo on flight deal image tile',
                  },
                  { carrierName: flightDeal.carrier.name },
                )}
              />
            </CarrierContainer>
          )}
        </ImageContainer>
      )}
      <DealContainer direction="vertical" gap={8}>
        <BodyText variant="large" weight="bold">{flightDeal.destinationName}</BodyText>
        <FlightDealPoints flightDeal={flightDeal} />
        {priceContent}
      </DealContainer>
    </FlightCardContainer>
  )
}

export default FlightDealImageTile
